import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";

import { cx } from "../lib/cx";
import { ToastContext, type ToastMessage, type ToastState, type ToastTone } from "./toast-context";

const INFO_TIMEOUT_MS = 4000;

/**
 * Save failures on a settings screen happen away from where the user is
 * looking — a toggle that snapped back explains nothing on its own. This is the
 * smallest thing that says why: a live region, a dismiss button, and a timer for
 * the messages that are only confirmations. Ninety lines, no dependency.
 */
export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = "info") => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, message, tone }]);
      if (tone === "info") {
        setTimeout(() => {
          dismiss(id);
        }, INFO_TIMEOUT_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo<ToastState>(() => ({ toasts, show, dismiss }), [dismiss, show, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-60 flex w-80 flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className={cx(
              "app-shadow pointer-events-auto flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm",
              toast.tone === "error"
                ? "border-danger bg-raised text-danger"
                : "border-line bg-raised text-ink",
            )}
          >
            <span className="min-w-0">{toast.message}</span>
            <button
              type="button"
              aria-label={`Dismiss: ${toast.message}`}
              className="shrink-0 text-xs text-muted hover:text-ink"
              onClick={() => {
                dismiss(toast.id);
              }}
            >
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
